import _ from 'lodash'
import Vue from 'vue'
import Vuex, {StoreOptions} from 'vuex'
import * as unigest from '@/modules/unigest'
import {getMachines, getUserConfig, Machine, Token, UserConfig} from "@/modules/unigest";
import {addModifier, ConfigMap, copy, flush, get, insert, load, replace, reset, set} from "@/modules/config";
import * as sigcom from '@/modules/sigcom'
import {SignatureRequest} from "@/modules/sigcom";

Vue.use(Vuex)

export class Document {
    public name = ''
    public data = ''

    /**
     * Crea una entidad documento a partir de un fichero
     *
     * @param file
     */
    public static fromFile (file: File): Promise<Document> {
        const document = new this()
        document.name = file.name

        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
                document.data = reader.result as string
                resolve(document)
            }
            reader.onerror = reason => reject(reason)

            reader.readAsDataURL(file)
        })
    }
}

/**
 * Parámetros de configuración para la aplicación
 */
export enum AppConfigParameters {
    AfterSignMessage = 'afterSignMessage',
    Machine = 'machine',
    DataProtectionDocument = 'dataProtectionDocument',
    UserConfig = 'userConfig',
    ValidateDNIFormat = 'validateDNIFormat',
}

function bootConfig () {
    addModifier(AppConfigParameters.DataProtectionDocument, (value) => {
        if (!value || !_.isObject(value)) {
            return null
        }

        return Object.assign(new Document(), value)
    })

    addModifier(AppConfigParameters.Machine, (value) => {
        if (!value) {
            return null
        }

        return Object.assign(new Machine(), value)
    })

    addModifier(AppConfigParameters.UserConfig, (value) => {
        if (!value) {
            return null
        }

        return Object.assign(new UserConfig(), value)
    })
}

export enum MutationTypes {
    SetToken = 'SET_TOKEN',
    SetMachines = 'SET_MACHINES',
    SetMachine = 'SET_MACHINE',
    SetUserConfig = 'SET_USER_CONFIG',
    SetSignatureRequest = 'SET_SIGNATURE_REQUEST',
    ToggleStarting = 'TOGGLE_STARTING',
    ToggleConnected = 'TOGGLE_CONNECTED',
    SetAppConfig = 'SET_APP_CONFIG',
    UpdateAppConfigOption = 'UPDATE_APP_CONFIG_OPTION',
}

export interface RootState {
    token: Token;
    machines: Machine[];
    /**
     * Maquina actualmente cargada por la aplicación
     */
    machine: Machine;
    config: UserConfig;
    /**
     * Parámetros de configuración globales de la aplicación
     */
    appConfig: ConfigMap;
    signatureRequest: SignatureRequest | null;
    /**
     * Indica si la aplicación esta iniciado
     */
    starting: boolean;

    connected: boolean;
}

const options: StoreOptions<RootState> = {
    state: {
        token: new Token(),
        machines: [],
        machine: new Machine(),
        config: new UserConfig(),
        signatureRequest: null,
        starting: true,
        connected: false,
        appConfig: {},
    },
    mutations: {
        /**
         * Guarda un token en el estado
         *
         * @param state
         * @param token
         */
        [MutationTypes.SetToken]: (state, token: Token) => state.token = token,
        /**
         * Establece la lista de maquinas registradas en Unigest
         *
         * @param state
         * @param machines
         */
        [MutationTypes.SetMachines]: (state, machines: Machine[]) => state.machines = machines,
        [MutationTypes.SetMachine]: (state, machine: Machine) => state.machine = machine,
        /**
         * Guarda en el estado el objeto de configuración con los parámetros del usuario
         *
         * @param state
         * @param config
         */
        [MutationTypes.SetUserConfig]: (state, config: UserConfig) => state.config = config,
        /**
         * Establece la solicitud de firma actualmente mostrada al usuario
         *
         * @param state
         * @param request
         */
        [MutationTypes.SetSignatureRequest]: (state, request: SignatureRequest | null) => state.signatureRequest = request,
        [MutationTypes.ToggleStarting]: (state, starting: boolean) => state.starting = starting,
        [MutationTypes.ToggleConnected]: (state, connected: boolean) => state.connected = connected,
        /**
         * Establece la configuración de la aplicación
         *
         * @param state
         * @param config
         */
        [MutationTypes.SetAppConfig]: (state, config: ConfigMap) => state.appConfig = config,
        /**
         * Actualiza un parámetro de configuración de la aplicación
         *
         * @param state
         * @param key
         * @param value
         */
        [MutationTypes.UpdateAppConfigOption]: (state, {key, value}) => {
            _.set(state.appConfig, key, value)
        }
    },
    actions: {
        /**
         * Realiza tareas para el inicio de la aplicación
         */
        async init ({commit, dispatch, getters}) {
            bootConfig()
            load()
            // Carga los parámetros de configuración globales de la aplicación
            commit(MutationTypes.SetAppConfig, copy())

            const machine = get('machine', null)
            const userConfig = get('userConfig', null)

            machine && commit(MutationTypes.SetMachine, machine)
            userConfig && commit(MutationTypes.SetUserConfig, userConfig)

            // Si la aplicación tiene configurada una maquina se iniciar la comunicación
            // con unigest para el proceso firmado
            if (machine) {
                await dispatch('initSignatureCommunication')
            }

            commit(MutationTypes.ToggleStarting, false)
        },
        /**
         * Inicia sesión en Unigest
         *
         * @param context
         * @param auth
         */
        async login ({commit}, {user, password}) {
            const token = await unigest.token(user, password)
            const config = await getUserConfig(user, password)

            // Guarda la información en el estado de la aplicación
            commit(MutationTypes.SetToken, token)
            commit(MutationTypes.SetUserConfig, config)

            set('savedUser', user)
            set('userConfig', config)
            flush()

            return token
        },
        /**
         * Carga en el estado de la aplicación las maquinas registrada en Unigest
         */
        async loadMachines ({commit}) {
            const machines = await getMachines()
            commit(MutationTypes.SetMachines, machines)
        },

        /**
         * Carga una maquina como principal
         */
        activateMachine ({commit}, machine: Machine) {
            // Guarda en el almacenamiento de la configuración
            insert('machine', machine)
            commit(MutationTypes.SetMachine, machine)
            commit(MutationTypes.SetAppConfig, copy())
        },
        /**
         * Inicia el mecanismo de comunicación para enviar las firmas a través del
         * protocolo WAMP
         */
        async initSignatureCommunication ({state, commit}) {
            if (!state.machine.name) {
                throw new Error('La maquina activa no es válida, no tiene nombre')
            }

            sigcom.onClose(() => commit(MutationTypes.ToggleConnected, false))
            sigcom.onOpen(() => commit(MutationTypes.ToggleConnected, true))

            await sigcom.init({
                machine: state.machine,
                serial: state.config.serial,
            })

            await sigcom.onSignature((request) => {
                commit(MutationTypes.SetSignatureRequest, request)
            })

            await sigcom.onFinish(() => {
                commit(MutationTypes.SetSignatureRequest, null)
            })
        },
        /**
         * Guarda los cambios realizados sobre la configuración de la aplicación
         * @param commit
         * @param config
         */
        saveAppConfig ({commit}, config: ConfigMap) {
            commit(MutationTypes.SetAppConfig, config)
            replace(config)
        },
        /**
         * Restablece la configuración de la app
         *
         * @param commit
         */
        async resetApp ({commit}) {
            // Elimina todas las suscripciones
            await sigcom.clear()

            commit(MutationTypes.SetAppConfig, {})
            commit(MutationTypes.SetUserConfig, {})
            commit(MutationTypes.SetMachines, [])
            commit(MutationTypes.SetMachine, new Machine())
            commit(MutationTypes.SetToken, new Token())
            commit(MutationTypes.SetSignatureRequest, null)
            reset()
        }
    },
    getters: {
        isConfigured: (state) => state.machine && !!state.machine.name,
        isConnected: () => sigcom.isConnected(),
        /**
         * Comprueba si la aplicación tiene configurado un documento de protección de datos
         *
         * @param state
         */
        hasDataProtectionDocument: (state) => {
            const value = state.appConfig[AppConfigParameters.DataProtectionDocument]
            if (!value) {
                return false
            }

            return value instanceof Document && value.data.startsWith('data:application/pdf;base64')
        }
    }
}

export default new Vuex.Store(options)
